import { supabase } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Indicadores de retrabalho, correções e tempo de fechamento.
 *
 * A agregação acontece no banco (migration 0007) e volta como um único
 * JSON. Com 170 clientes o histórico passa de 60 mil linhas por ano —
 * trazer isso cru para o navegador somar seria lento e comeria a cota de
 * tráfego do plano gratuito.
 */

async function rpc(nome, args) {
  const { data, error } = await supabase.rpc(nome, args);
  if (error) throw new Error(error.message);
  return data;
}

const vazio = (extra = {}) => ({
  total: 0, por_empresa: [], por_item: [], por_etapa: [],
  por_colaborador: [], por_mes: [], ...extra,
});

/* ------------------------------------------------------------------ */
/* Retrabalho                                                          */
/* ------------------------------------------------------------------ */

/**
 * Retrabalho = algo que estava pronto deixou de estar.
 * Item que era OK e voltou para Parcial/Pendente, ou etapa do checklist
 * que estava feita e foi desmarcada. Avançar de Pendente para OK é
 * trabalho normal e não entra.
 */
export async function retrabalho({ de = null, ate = null, incluirAutocorrecao = false } = {}) {
  if (Demo.emDemonstracao) return retrabalhoDemo({ de, ate });   // guarda do modo demonstração
  return rpc('painel_retrabalho', { p_de: de, p_ate: ate, p_incluir_autocorrecao: incluirAutocorrecao });
}

function retrabalhoDemo({ de, ate }) {
  const eventos = Demo.historico.filter((h) => {
    if (!['item', 'checklist'].includes(h.tipo)) return false;
    const voltou =
      (h.status_anterior === 'ok' && h.status_novo !== 'ok') ||
      (h.status_anterior === 'feito' && h.status_novo === 'não feito');
    if (!voltou) return false;
    const dia = h.criado_em.slice(0, 10);
    return (!de || dia >= de) && (!ate || dia <= ate);
  });

  const agrupar = (lista, chave) => {
    const m = new Map();
    lista.forEach((h) => {
      const k = chave(h) || '—';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()]
      .map(([nome, n]) => ({ nome, n }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 10);
  };

  return {
    total: eventos.length,
    empresas_afetadas: new Set(eventos.map((h) => h.empresa_id)).size,
    por_empresa: agrupar(eventos, (h) => h.empresa?.nome),
    por_item: agrupar(eventos.filter((h) => h.tipo === 'item'), (h) => h.descricao),
    por_etapa: agrupar(eventos.filter((h) => h.tipo === 'checklist'), (h) => h.descricao),
    por_colaborador: agrupar(eventos, (h) => h.colaborador?.nome),
    por_mes: [...eventos.reduce((m, h) => {
      const mes = h.criado_em.slice(0, 7);
      return m.set(mes, (m.get(mes) || 0) + 1);
    }, new Map()).entries()]
      .map(([mes, n]) => ({ mes, n }))
      .sort((a, b) => a.mes.localeCompare(b.mes)),
  };
}

/* ------------------------------------------------------------------ */
/* Correções apontadas na revisão                                      */
/* ------------------------------------------------------------------ */

export async function correcoes({ de = null, ate = null } = {}) {
  if (Demo.emDemonstracao) return correcoesDemo({ de, ate });   // guarda do modo demonstração
  return rpc('painel_correcoes', { p_de: de, p_ate: ate });
}

function correcoesDemo({ de, ate }) {
  const dentro = (c) => {
    const dia = c.criado_em.slice(0, 10);
    return (!de || dia >= de) && (!ate || dia <= ate);
  };

  const todos = Demo.comentarios.filter(dentro).map((c) => {
    const item = Demo.itens.find((i) => i.id === c.item_id);
    const f = item && Demo.fechamentos.find((x) => x.id === item.fechamento_id);
    const e = f && Demo.empresas.find((x) => x.id === f.empresa_id);
    return {
      ...c,
      item_nome: item?.nome,
      empresa_nome: e?.nome,
      responsavel_nome: e?.responsaveis?.[0]?.nome,
    };
  });

  const corr = todos.filter((c) => c.tipo === 'correcao');
  const agrupar = (lista, chave) => {
    const m = new Map();
    lista.forEach((c) => {
      const k = chave(c) || '—';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].map(([nome, n]) => ({ nome, n })).sort((a, b) => b.n - a.n).slice(0, 10);
  };

  return {
    total: todos.length,
    correcoes: corr.length,
    orientacoes: todos.filter((c) => c.tipo === 'orientacao').length,
    duvidas: todos.filter((c) => c.tipo === 'duvida').length,
    em_aberto: todos.filter((c) => !c.resolvido).length,
    correcoes_em_aberto: corr.filter((c) => !c.resolvido).length,
    por_empresa: agrupar(corr, (c) => c.empresa_nome),
    por_item: agrupar(corr, (c) => c.item_nome),
    por_responsavel: agrupar(corr, (c) => c.responsavel_nome),
    por_mes: [...corr.reduce((m, c) => {
      const mes = c.criado_em.slice(0, 7);
      return m.set(mes, (m.get(mes) || 0) + 1);
    }, new Map()).entries()].map(([mes, n]) => ({ mes, n })).sort((a, b) => a.mes.localeCompare(b.mes)),
  };
}

/* ------------------------------------------------------------------ */
/* Tempo de fechamento                                                 */
/* ------------------------------------------------------------------ */

export async function tempoFechamento({ ano = null } = {}) {
  if (Demo.emDemonstracao) return tempoFechamentoDemo(ano);   // guarda do modo demonstração
  return rpc('painel_tempo_fechamento', { p_ano: ano });
}

function tempoFechamentoDemo(ano) {
  const base = Demo.fechamentos
    .filter((f) => f.status === 'concluido' && f.concluido_em)
    .filter((f) => !ano || f.competencia.startsWith(String(ano)))
    .map((f) => {
      const fim = new Date(f.competencia + 'T00:00');
      fim.setMonth(fim.getMonth() + 1);
      const dias = Math.round((new Date(f.concluido_em) - fim) / 86400000);
      const e = Demo.empresas.find((x) => x.id === f.empresa_id);
      return { empresa: e?.nome || '—', dias };
    });

  // Acima de 120 dias é passivo sendo regularizado, não ritmo normal.
  const normal = base.filter((b) => b.dias >= 0 && b.dias <= 120);
  const porEmpresa = new Map();
  normal.forEach((b) => {
    const atual = porEmpresa.get(b.empresa) || { soma: 0, n: 0 };
    porEmpresa.set(b.empresa, { soma: atual.soma + b.dias, n: atual.n + 1 });
  });

  return {
    amostra: normal.length,
    descartadas: base.length - normal.length,
    media_dias: normal.length ? Math.round(normal.reduce((s, b) => s + b.dias, 0) / normal.length) : null,
    por_empresa: [...porEmpresa.entries()]
      .filter(([, v]) => v.n >= 2)
      .map(([nome, v]) => ({ nome, media: Math.round(v.soma / v.n), fechamentos: v.n }))
      .sort((a, b) => b.media - a.media)
      .slice(0, 15),
  };
}

export { vazio };

/**
 * Os casos por trás dos números.
 *
 * Os painéis devolviam só totais — "3 retrabalhos na empresa X" — e o
 * caso individual nunca chegava à tela. Saber que houve 3 não ajuda; ver
 * que os 3 foram na Conciliação da mesma empresa, no mesmo mês, ajuda.
 *
 * Restrito à gestão no próprio banco: o caso diz quem desfez o quê.
 */
export async function casosRetrabalho({ de = null, ate = null, empresa = null, item = null, colaborador = null, incluirAutocorrecao = false } = {}) {
  if (Demo.emDemonstracao) return casosRetrabalhoDemo({ de, ate, empresa, item });   // guarda do modo demonstração

  return rpc('retrabalho_casos', {
    p_de: de, p_ate: ate, p_empresa: empresa, p_item: item, p_colaborador: colaborador,
    p_incluir_autocorrecao: incluirAutocorrecao,
  });
}

export async function casosCorrecoes({ de = null, ate = null, empresa = null, tipo = null, resolvido = null } = {}) {
  if (Demo.emDemonstracao) return casosCorrecoesDemo({ de, ate, empresa, tipo, resolvido });   // guarda do modo demonstração

  return rpc('correcoes_casos', {
    p_de: de, p_ate: ate, p_empresa: empresa, p_tipo: tipo, p_resolvido: resolvido,
  });
}

/*
 * As mesmas duas listas, montadas do lado do navegador.
 *
 * Existem para a demonstração poder mostrar o caminho completo — clicar no
 * número e chegar no caso — antes de haver um único retrabalho real
 * registrado no banco.
 */
function casosRetrabalhoDemo({ de, ate, empresa, item }) {
  return Demo.historico
    .filter((h) => {
      if (!['item', 'checklist'].includes(h.tipo)) return false;
      const voltou =
        (h.status_anterior === 'ok' && h.status_novo !== 'ok') ||
        (h.status_anterior === 'feito' && h.status_novo === 'não feito');
      if (!voltou) return false;
      const dia = h.criado_em.slice(0, 10);
      if (de && dia < de) return false;
      if (ate && dia > ate) return false;
      if (empresa && h.empresa?.nome !== empresa) return false;
      if (item && h.descricao !== item) return false;
      return true;
    })
    .map((h) => ({
      empresa: h.empresa?.nome || '—',
      competencia: h.competencia,
      tipo: h.tipo,
      item: h.descricao,
      status_anterior: h.status_anterior,
      status_novo: h.status_novo,
      colaborador: h.colaborador?.nome || '—',
      quando: h.criado_em,
    }))
    .sort((a, b) => b.quando.localeCompare(a.quando))
    .slice(0, 200);
}

function casosCorrecoesDemo({ de, ate, empresa, tipo, resolvido }) {
  return Demo.comentarios
    .map((c) => {
      const i = Demo.itens.find((x) => x.id === c.item_id);
      const fe = i && Demo.fechamentos.find((x) => x.id === i.fechamento_id);
      const e = fe && Demo.empresas.find((x) => x.id === fe.empresa_id);
      return {
        empresa: e?.nome || '—',
        competencia: fe?.competencia || null,
        item: i?.nome || '—',
        tipo: c.tipo,
        texto: c.texto,
        autor: c.autor?.nome || Demo.colaboradores.find((x) => x.id === c.autor_id)?.nome || '—',
        resolvido: !!c.resolvido,
        quando: c.criado_em,
      };
    })
    .filter((c) => {
      const dia = c.quando.slice(0, 10);
      if (de && dia < de) return false;
      if (ate && dia > ate) return false;
      if (empresa && c.empresa !== empresa) return false;
      if (tipo && c.tipo !== tipo) return false;
      if (resolvido !== null && resolvido !== undefined && c.resolvido !== resolvido) return false;
      return true;
    })
    .sort((a, b) => b.quando.localeCompare(a.quando))
    .slice(0, 200);
}
